import React from "react";
import { toast } from "react-toastify";
import { makeErrorData } from "./generalUtils";
import { ErrorData, StandardErrorData } from "../store/bugReport/types";
import BugReportToast from "../components/atoms/BugReportToast";
import {
  errorToastConfig,
  infoToastConfig,
  successToastConfig,
  warningToastConfig,
} from "../config/toasting";
import { TemplateValues } from "../hooks/useFormatMsg";
import {
  getMessageFromError,
  makeErrorToastMsg,
  toastifyResponseTuples,
} from "./newToasting";
import { callableObject } from "./callableObject";
import { formatMsg } from "./intl/intlFormmaters";
import { prefixes } from "../assets/localization/utils";
import { IntlMessageId, UnprefixedKeys } from "../assets/localization/types";

export type ToastSuccessIdBase<ID = UnprefixedKeys["Toast"]> =
  ID extends `${infer Base}.success` ? Base : never;

export type ToastFailureIdBase<ID = UnprefixedKeys["Toast"]> =
  ID extends `${infer Base}.failure` ? Base : never;

export type ToastWarningIdBase<ID = UnprefixedKeys["Toast"]> =
  ID extends `${infer Base}.warning` ? Base : never;

// info = pro info toasty - nemá success ani failure na konci
export type ToastInfoIdBase<ID = UnprefixedKeys["Toast"]> = ID extends
  | `${infer Base}.failure`
  | `${infer Base}.warning`
  | `${infer Base}.success`
  ? never
  : ID;

const success = (msg: string) => toast.success(msg, successToastConfig);
const info = (msg: string) => toast.info(msg, infoToastConfig);
const warning = (msg: string, err?: unknown | unknown[]) => {
  const errorObj = makeErrorData<StandardErrorData>(err);
  const toastComponentInstance = (
    <BugReportToast errorStack={errorObj} msg={msg} />
  );
  toast.warning(toastComponentInstance, warningToastConfig);
};
const error = (msg: string, err: unknown | unknown[]) => {
  const errorObj = makeErrorData<StandardErrorData>(err);
  const toastComponentInstance = (
    <BugReportToast errorStack={errorObj} msg={msg} />
  );
  return toast.error(toastComponentInstance, errorToastConfig);
};
// no stack = nemám k dispozici error objekt, vykreslím jen normální modal bez možnosti bug reportu
const errorNoStack = (msg: string) => toast.error(msg, errorToastConfig);

// From intl id
const successFromIntlId = (
  intlId: IntlMessageId,
  intlParams?: TemplateValues
) => success(formatMsg(intlId, intlParams));

const infoFromIntlId = (intlId: IntlMessageId, intlParams?: TemplateValues) =>
  info(formatMsg(intlId, intlParams));

const warningFromIntlId = (
  intlId: IntlMessageId,
  intlParams?: TemplateValues
) => warning(formatMsg(intlId, intlParams));

export const errorFromIntlId = (
  intlId: IntlMessageId,
  err: unknown | unknown[],
  intlParams?: TemplateValues,
  returnMessage?: boolean
) => {
  const baseMsg = formatMsg(intlId, intlParams);
  const errorDetails = getMessageFromError(err);
  const fullMsg = makeErrorToastMsg(baseMsg, errorDetails);
  if (returnMessage) {
    return fullMsg;
  }
  return error(fullMsg, err);
};

// no stack = nemám k dispozici error objekt, vykreslím jen normální modal bez možnosti bug reportu
const errorNoStackFromIntlId = (
  intlId: IntlMessageId,
  errorDetails: string | string[] | null = null,
  intlParams?: TemplateValues
) => {
  const baseMsg = formatMsg(intlId, intlParams);
  const fullMsg = makeErrorToastMsg(baseMsg, errorDetails ?? undefined);
  errorNoStack(fullMsg);
};

// From intl base
// zavolá .fromIntlId s intlId = `toast.${intlBase}.success`
const successFromIntlBase = (
  intlBase: ToastSuccessIdBase,
  intlParams?: TemplateValues
) => {
  const intlId = `${prefixes.toast}.${intlBase}.success` as const;
  successFromIntlId(intlId, intlParams);
};

// zavolá .fromIntlId s intlId = `toast.${intlBase}`
const infoFromIntlBase = (
  intlBase: ToastInfoIdBase,
  intlParams?: TemplateValues
) => {
  const intlId = `${prefixes.toast}.${intlBase}` as const;
  infoFromIntlId(intlId, intlParams);
};

// zavolá .fromIntlId s intlId = `toast.${intlBase}.error`
export const errorFromIntlBase = (
  intlBase: ToastFailureIdBase,
  err: unknown | unknown[],
  intlParams?: TemplateValues
) => {
  const intlId = `${prefixes.toast}.${intlBase}.failure` as const;
  errorFromIntlId(intlId, err, intlParams);
};

// zavolá .fromIntlId s intlId = `toast.${intlBase}.warning`
const warningFromIntlBase = (
  intlBase: ToastInfoIdBase,
  intlParams?: TemplateValues
) => {
  const intlId = `${prefixes.toast}.${intlBase}` as const;
  warningFromIntlId(intlId, intlParams);
};

// zavolá .fromIntlId s intlId = `toast.${intlBase}.error`
//
// no stack = nemám k dispozici error objekt, vykreslím jen normální modal bez možnosti bug reportu
const errorNoStackFromIntlBase = (
  intlBase: ToastFailureIdBase,
  errorDetails: string | string[] | null = null,
  intlParams?: TemplateValues
) =>
  errorNoStackFromIntlId(
    `${prefixes.toast}.${intlBase}.failure` as const,
    errorDetails,
    intlParams
  );

const flowioast = {
  error: callableObject(error, {
    noStack: callableObject(errorNoStack, {
      fromIntlId: errorNoStackFromIntlId,
      fromIntlBase: errorNoStackFromIntlBase,
    }),
    fromIntlId: errorFromIntlId,
    fromIntlBase: errorFromIntlBase,
    fromResponseMessage: (msg: string, err?: ErrorData) => {
      error(msg, {
        ...err,
        name: err && formatMsg(err.name as IntlMessageId),
      });
    },
  }),
  success: callableObject(success, {
    fromIntlId: successFromIntlId,
    fromIntlBase: successFromIntlBase,
    fromResponseMessage: (msg: string) => success(msg),
  }),
  warning: callableObject(warning, {
    fromIntlId: warningFromIntlId,
    fromIntlBase: warningFromIntlBase,
    fromResponseMessage: (msg: string, err?: ErrorData) =>
      warning(msg, {
        ...err,
        name: err && formatMsg(err.name as IntlMessageId),
      }),
  }),
  info: callableObject(info, {
    fromIntlId: infoFromIntlId,
    fromIntlBase: infoFromIntlBase,
  }),
  fromResponseTuples: toastifyResponseTuples,
};

export default flowioast;
